/**
 * 机构详情页组件
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Button, Spin, Tag, Card } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BankOutlined } from '@ant-design/icons';
import { institutionService } from '../../../../services/institutionService';
import { useDict } from '../../../../hooks/useUnifiedDict';
import { DICT_TYPES } from '../../../../constants/dictTypes';
import './InstitutionDetailPage.css';

interface InstitutionDetail {
  id: number;
  name: string;
  type: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  description?: string;
  website?: string;
  logo?: string;
  status: number;
  createTime?: string;
  updateTime?: string;
}

const InstitutionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  
  const [institution, setInstitution] = useState<InstitutionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  
  // 使用数据字典获取机构类型
  const { dictData: institutionTypeDict, getLabelByValue } = useDict(DICT_TYPES.INSTITUTION_TYPE);

  // 获取机构类型标签 - 必须在组件顶层，在任何早期返回之前
  const typeLabel = useMemo(() => {
    if (!institution || !institution.type) return '';
    const label = getLabelByValue(institution.type);
    // 如果字典未加载或找不到，尝试直接使用type值（可能已经是中文）
    return label && label !== institution.type ? label : institution.type;
  }, [institution?.type, getLabelByValue, institutionTypeDict]);

  // 获取机构类型标签颜色
  const getTypeColor = (type: string): string => {
    const colorMap: Record<string, string> = {
      '代理机构': '#52c41a',
      '法律咨询': '#fa8c16',
      '评估机构': '#722ed1',
      '金融机构': '#13c2c2',
      '保险机构': '#eb2f96',
      '高校、科研院所': '#fa541c',
    };
    return colorMap[type] || '#1890ff';
  };

  const typeColor = getTypeColor(typeLabel);

  // 从URL路径中提取ID
  useEffect(() => {
    const loadInstitutionDetail = async () => {
      let id: number = 0;
      const pathname = location.pathname;
      
      // 匹配 /show/institutions/123 格式
      const match = pathname.match(/\/(show\/)?institutions\/(\d+)$/);
      if (match && match[2]) {
        id = parseInt(match[2]);
      } else if (params.id) {
        id = parseInt(params.id);
      }
      
      if (!id || isNaN(id)) {
        setError('无效的机构ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setInstitution(null);

      try {
        const response = await institutionService.getById(id);
        
        if (response && response.code === 200 && response.data) {
          const data = response.data;
          setInstitution(data as InstitutionDetail);
        } else {
          setError(response?.message || '机构不存在');
        }
      } catch (apiError: any) {
        if (apiError.response) {
          if (apiError.response.status === 404) {
            setError('机构不存在');
          } else {
            setError(`加载失败: ${apiError.response.data?.message || apiError.message}`);
          }
        } else {
          setError(`加载失败: ${apiError.message || '网络错误'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadInstitutionDetail();
  }, [location.pathname, params.id]);

  // 处理图片路径
  const getLogoUrl = (logo?: string): string => {
    if (!logo) return '';
    if (logo.startsWith('data:') || logo.startsWith('http://') || logo.startsWith('https://')) {
      return logo;
    }
    return logo.startsWith('/') ? logo : '/' + logo;
  };

  // 加载中状态
  if (loading) {
    return (
      <div className="institution-detail-page">
        <div className="institution-detail-content">
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  // 错误或数据不存在
  if (error || !institution) {
    return (
      <div className="institution-detail-page">
        <div className="institution-detail-content">
          <Button onClick={() => navigate('/show/institutions')} className="back-button">
            ← 返回机构列表
          </Button>
          <h1>机构不存在</h1>
          <p>抱歉，您访问的机构不存在。</p>
          <Button onClick={() => navigate('/show/institutions')}>返回机构列表</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="institution-detail-page">
      <div className="institution-detail-content">
        {/* 返回按钮 */}
        <Button 
          onClick={() => navigate('/show/institutions')} 
          className="back-button"
          style={{ marginBottom: '24px' }}
        >
          ← 返回机构列表
        </Button>

        {/* 机构信息卡片 - 合并展示 */}
        <Card className="institution-detail-card">
          <div className="institution-detail-wrapper">
            {/* Logo和名称区域 */}
            <div className="institution-header-section">
              <div className="institution-logo-section">
                {institution.logo && !logoError ? (
                  <img 
                    src={getLogoUrl(institution.logo)}
                    alt={institution.name}
                    className="institution-logo-large"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="institution-logo-placeholder">
                    <BankOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                  </div>
                )}
              </div>
              <div className="institution-title-section">
                <h1 className="institution-name">{institution.name}</h1>
                <Tag color={typeColor} className="institution-type-tag">
                  {typeLabel}
                </Tag>
              </div>
            </div>

            {/* 内容区域 - 简洁的标题+内容格式 */}
            <div className="institution-content-section">
              {/* 机构简介 */}
              {institution.description && (
                <div className="institution-content-item">
                  <h3 className="institution-content-title">机构简介</h3>
                  <div className="institution-content-text">
                    {institution.description}
                  </div>
                </div>
              )}
              
              {/* 官方网站 */}
              {institution.website && (
                <div className="institution-content-item">
                  <h3 className="institution-content-title">官方网站</h3>
                  <div className="institution-content-text">
                    <a 
                      href={institution.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="institution-link"
                    >
                      {institution.website}
                    </a>
                  </div>
                </div>
              )}
              
              {/* 机构地址 */}
              {institution.address && (
                <div className="institution-content-item">
                  <h3 className="institution-content-title">机构地址</h3>
                  <div className="institution-content-text">
                    {institution.address}
                  </div>
                </div>
              )}
              
              {/* 联系人、联系电话、联系邮箱 - 同一行显示 */}
              {(institution.contactPerson || institution.contactPhone || institution.contactEmail) && (
                <div className="institution-content-item">
                  <h3 className="institution-content-title">联系方式</h3>
                  <div className="institution-contact-row">
                    {institution.contactPerson && (
                      <div className="institution-contact-item">
                        <span className="contact-label">联系人：</span>
                        <span className="contact-value">{institution.contactPerson}</span>
                      </div>
                    )}
                    {institution.contactPhone && (
                      <div className="institution-contact-item">
                        <span className="contact-label">联系电话：</span>
                        <a href={`tel:${institution.contactPhone}`} className="institution-link contact-value">
                          {institution.contactPhone}
                        </a>
                      </div>
                    )}
                    {institution.contactEmail && (
                      <div className="institution-contact-item">
                        <span className="contact-label">联系邮箱：</span>
                        <a href={`mailto:${institution.contactEmail}`} className="institution-link contact-value">
                          {institution.contactEmail}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="institution-actions">
              {institution.website && (
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => window.open(institution.website, '_blank')}
                  className="action-button"
                >
                  访问官网
                </Button>
              )}
              <Button 
                size="large"
                onClick={() => navigate('/show/institutions')}
                className="action-button"
              >
                返回列表
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default InstitutionDetailPage;

